import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { verifyOAuthState, cleanupOAuthState } from '../utils/oauth';

export default function GmailCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        setError('Invalid callback parameters');
        return;
      }

      try {
        const userId = await verifyOAuthState(state);
        if (!userId) {
          setError('Invalid or expired state');
          return;
        }

        const { data: tokens, error: tokenError } = await supabase.functions.invoke('exchange-gmail-token', {
          body: { 
            code,
            redirectUri: `${window.location.origin}/auth/gmail/callback`
          }
        });

        if (tokenError) throw tokenError;

        const { error: saveError } = await supabase
          .from('nrm_gmail_credentials')
          .insert([{
            user_id: userId,
            email: tokens.email,
            access_token: tokens.access_token,
            refresh_token: tokens.refresh_token,
            token_expiry: new Date(Date.now() + tokens.expires_in * 1000).toISOString()
          }]);

        if (saveError) throw saveError;

        await cleanupOAuthState(state);
        navigate('/settings', { replace: true });
      } catch (err) {
        console.error('Gmail callback error:', err);
        setError('Failed to connect Gmail account');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => navigate('/settings')}
            className="btn btn-primary"
          >
            Back to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
        <p className="text-gray-400">Connecting your Gmail account...</p>
      </div>
    </div>
  );
}